import { useLocation, useNavigate } from 'react-router-dom';
import { brand, fontFamily, tg } from '../../theme/tokens';

type TabId = 'create' | 'library' | 'profile';

const TABS: Array<{ id: TabId; path: string; label: string; icon: 'sparkles' | 'grid' | 'user' }> = [
  { id: 'create', path: '/', label: 'Create', icon: 'sparkles' },
  { id: 'library', path: '/library', label: 'Library', icon: 'grid' },
  { id: 'profile', path: '/profile', label: 'Profile', icon: 'user' },
];

/**
 * Floating pill bottom-nav. Shown only on tab roots (Create/Library/Profile);
 * sub-pages (Generating, Result, etc.) hide it.
 *
 * Position: fixed bottom: 16px, centered horizontally. ~280px wide.
 * Uses backdrop-filter blur for glassmorphism over content.
 */
export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const active: TabId = location.pathname.startsWith('/library')
    ? 'library'
    : location.pathname.startsWith('/profile')
    ? 'profile'
    : 'create';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 280,
        height: 60,
        borderRadius: 999,
        background: 'var(--tgui--secondary_bg_color)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${tg.divider}`,
        boxShadow: '0 12px 40px rgba(40,20,30,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 12px',
        zIndex: 50,
      }}
    >
      {TABS.map((tab) => {
        const on = tab.id === active;
        return (
          <button
            key={tab.id}
            onClick={() => navigate(tab.path)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              flex: 1,
              height: 48,
              fontFamily,
            }}
          >
            <div
              style={{
                width: 36,
                height: 28,
                borderRadius: 999,
                background: on ? `${brand.solid}1A` : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 200ms',
              }}
            >
              <NavIcon name={tab.icon} active={on} />
            </div>
            <span
              style={{
                fontSize: 10,
                fontWeight: on ? 600 : 500,
                color: on ? brand.solid : 'var(--tgui--hint_color)',
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function NavIcon({ name, active }: { name: 'sparkles' | 'grid' | 'user'; active: boolean }) {
  const stroke = active ? brand.solid : 'var(--tgui--hint_color)';
  const sw = active ? 2.4 : 2;
  const props = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: sw,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'sparkles':
      return (
        <svg {...props}>
          <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
          <path d="M19 14l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7.7-1.9z" />
        </svg>
      );
    case 'grid':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case 'user':
      return (
        <svg {...props}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
        </svg>
      );
  }
}
